import Chart from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import * as fabric from 'office-ui-fabric-react';
import React, { FC, useEffect, useState } from 'react';
import * as api from '../../../api/generated';
import { KendoLoading } from '../../../components';
import { ShowErrorModal } from '../../queueManagement/components/ShowErrorModal';
import { INugisStatisticFilter } from './NugisStatistics';

interface IProps {
    timestamp: string | undefined;
    filter: INugisStatisticFilter;
}

export interface IStatisticGraphsData {
    graphDataByServices: IStatisticGraphsDataItem[] | null;
    graphDataByUser: IStatisticGraphsDataItem[] | null;
}

interface IStatisticGraphsDataItem {
    name: string;
    count: number;
}

const NugisStatisticsGraphs: FC<IProps> = props => {
    const [error, setError] = useState<string | null>(null);
    const [graphsData, setGraphsData] = useState<IStatisticGraphsData | null>(null);

    const fetchData = async () => {
        try {
            setGraphsData(null);
            const resp = await api.statistic.getNugisStatisticsGraphsData({
                ...props.filter,
                practitioners: props.filter.practitioners.map(x => x.text),
                dateFrom: moment(props.filter.dateFrom!),
                dateTo: moment(props.filter.dateTo!),
                timeFrom: props.filter.timeFrom!,
                timeTo: props.filter.timeTo!
            });
            setGraphsData({
                graphDataByServices: resp!.graphDataByServices.map(x => {
                    return { name: x.name!, count: x.count };
                }),
                graphDataByUser: resp!.graphDataByUser.map(x => {
                    return { name: x.name!, count: x.count };
                })
            });
        } catch (err) {
            setError(err.error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [props.timestamp]);

    const xAxisMaxValue = (array: number[]): number => {
        var maxValueInArray = Math.max.apply(null, array);
        var multiplier = maxValueInArray < 1000 ? 100 : maxValueInArray < 10000 ? 1000 : 10000;
        var maxValue = Math.ceil(maxValueInArray / multiplier) * multiplier;
        return maxValue - maxValueInArray < maxValue / 4 ? maxValue + multiplier : maxValue;
    };

    var graphByServices: Chart;
    useEffect(() => {
        if (!graphsData || !graphsData.graphDataByServices) return;

        if (graphByServices) {
            graphByServices.destroy();
        }

        var canvas: any = document.getElementById('graphByServicesChart');
        if (canvas == null) return;

        var ctx = canvas.getContext('2d');
        graphByServices = new Chart(ctx, {
            plugins: [ChartDataLabels],
            type: 'horizontalBar',
            data: {
                labels: graphsData?.graphDataByServices?.map(x => x.name),
                datasets: [
                    {
                        label: 'Pagal paslaugą',
                        data: graphsData?.graphDataByServices.map(x => x.count),
                        backgroundColor: 'rgba(16,110,190,0.2)',
                        borderColor: 'rgba(16,110,190,1)',
                        borderWidth: 1,
                        datalabels: {
                            align: 'end',
                            anchor: 'end',
                            offset: 4,
                            font: {
                                weight: 'bold'
                            }
                        }
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    xAxes: [
                        {
                            ticks: { max: xAxisMaxValue(graphsData.graphDataByServices.map(x => x.count)) }
                        }
                    ]
                },
                title: {
                    display: true,
                    text: 'Pagal paslaugą'
                },
                legend: {
                    display: false
                }
            }
        });
    }, [graphsData?.graphDataByServices]);

    var graphByUsers: Chart;
    useEffect(() => {
        if (!graphsData || !graphsData.graphDataByUser) return;

        if (graphByUsers) {
            graphByUsers.destroy();
        }

        var canvas: any = document.getElementById('graphByUsersChart');
        if (canvas == null) return;

        var ctx = canvas.getContext('2d');
        graphByUsers = new Chart(ctx, {
            plugins: [ChartDataLabels],
            type: 'horizontalBar',
            data: {
                labels: graphsData?.graphDataByUser?.map(x => x.name),
                datasets: [
                    {
                        label: 'Pagal darbuotoją',
                        data: graphsData?.graphDataByUser.map(x => x.count),
                        backgroundColor: 'rgba(16,110,190,0.2)',
                        borderColor: 'rgba(16,110,190,1)',
                        borderWidth: 1,
                        datalabels: {
                            align: 'end',
                            anchor: 'end',
                            offset: 4,
                            font: {
                                weight: 'bold'
                            }
                        }
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    xAxes: [
                        {
                            ticks: { max: xAxisMaxValue(graphsData.graphDataByUser.map(x => x.count)) }
                        }
                    ]
                },
                title: {
                    display: true,
                    text: 'Pagal darbuotoją'
                },
                legend: {
                    display: false
                }
            }
        });
    }, [graphsData?.graphDataByUser]);

    return (
        <>
            {graphsData && (
                <div className="ms-Grid" dir="ltr">
                    {graphsData?.graphDataByServices!.length > 1 && (
                        <div className="ms-Grid-row">
                            <div
                                className="chart-container"
                                style={{
                                    position: 'relative',
                                    height: `${512 + graphsData.graphDataByServices!.length * 16}px`,
                                    width: '1266px'
                                }}
                            >
                                <canvas id="graphByServicesChart"></canvas>
                            </div>
                        </div>
                    )}
                    {graphsData?.graphDataByUser!.length > 0 && (
                        <div className="ms-Grid-row">
                            <div
                                className="chart-container"
                                style={{
                                    position: 'relative',
                                    height: `${512 + graphsData.graphDataByUser!.length * 16}px`,
                                    width: '1266px'
                                }}
                            >
                                <canvas id="graphByUsersChart"></canvas>
                            </div>
                        </div>
                    )}
                    <div className="ms-Grid-row">
                        <fabric.Separator />
                    </div>
                </div>
            )}
            {(graphsData == null || graphsData == undefined) && <KendoLoading />}
            {error && <ShowErrorModal logout={true} error={error}></ShowErrorModal>}
        </>
    );
};

export default NugisStatisticsGraphs;
